import React, { useEffect, useState, useCallback } from 'react';
import { Handle, Position, useNodes, useEdges } from '@xyflow/react';
import { 
  Popconfirm,
  Modal,
  Space, 
  Card, 
  Input, 
  Select, 
  Typography, 
  Button, 
  Form, 
  message, 
  InputNumber, 
  Switch, 
  Table, 
  Dropdown, 
  Menu,
  Tabs,
  Tag,
  Tooltip,
  Alert
} from 'antd';
import { 
  DeleteOutlined, 
  PlusOutlined, 
  DownOutlined, 
  SendOutlined, 
  InfoCircleOutlined, 
  ReloadOutlined,
  SaveOutlined,
  LinkOutlined,
  FileTextOutlined,
  SettingOutlined,
  FieldTimeOutlined,
  DatabaseOutlined,
  CodeOutlined,
  CloudOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { Icon } from '@iconify/react';
import { adminInstance } from '../api/axiosConfig';
import config from '../../config';
import NodeInputHandler from '../common/NodeInputHandler';
import GenericNodeContent from '../common/GenericNodeContent';
import { handleGoogleAPIError, GoogleIntegrationStatus } from '../utils/handleGoogleAPIError';

const { Option } = Select;
const { Paragraph, Text } = Typography;
const { TabPane } = Tabs;

const OutputNode = ({ id, data, isConnectable, workflowId  }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [existingTables, setExistingTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(data.selectedTable || '');
  const [newTableName, setNewTableName] = useState('');
  const [form] = Form.useForm();
  const [webhookUrl, setWebhookUrl] = useState(data.webhookUrl || '');
  const [isTestingWebhook, setIsTestingWebhook] = useState(false);
  const [nodeName, setNodeName] = useState(data.nodeName || `Output ${id}`);
  const [displayOutput, setDisplayOutput] = useState(data.displayOutput || "Output will be displayed here after execution");
  const [outputType, setOutputType] = useState(data.outputType || 'display');
  const [workflowIdState, setWorkflowIdState] = useState(data.workflowId || workflowId);
  const [spreadsheets, setSpreadsheets] = useState([]);
  const [selectedSpreadsheet, setSelectedSpreadsheet] = useState(data.selectedSpreadsheet || '');
  const [sheetTabs, setSheetTabs] = useState([]);
  const [selectedSheetTab, setSelectedSheetTab] = useState(data.selectedSheetTab || '');
  const [columnMappings, setColumnMappings] = useState(data.columnMappings || {});
  const [sheetColumns, setSheetColumns] = useState([]);
  const [driveItems, setDriveItems] = useState({ folders: [] });
  const [selectedDriveFolder, setSelectedDriveFolder] = useState(data.selectedDriveFolder || '');
  const [isFetchingFolders, setIsFetchingFolders] = useState(false);
  const [sharingLevel, setSharingLevel] = useState(data.sharingLevel || 'private');
  const [driveFileName, setDriveFileName] = useState(data.driveFileName || '');
  const [driveFileFormat, setDriveFileFormat] = useState(data.driveFileFormat || 'txt');
  const [driveItemType, setDriveItemType] = useState(data.driveItemType || '');
  const [delayEnabled, setDelayEnabled] = useState(data.delayEnabled || false);
  const [delayValue, setDelayValue] = useState(data.delayValue || 1);
  const [delayUnit, setDelayUnit] = useState(data.delayUnit || 'minutes');
  const [columns, setColumns] = useState(data.columns || []);
  const [isColumnModalVisible, setIsColumnModalVisible] = useState(false);
  const [isNewTable, setIsNewTable] = useState(false);
  const [webhookKeys, setWebhookKeys] = useState(data.webhookKeys || []);
  const [isWebhookKeyModalVisible, setIsWebhookKeyModalVisible] = useState(false);
  const [newWebhookKey, setNewWebhookKey] = useState('');
  const [isExplanationModalVisible, setIsExplanationModalVisible] = useState(false);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [driveContent, setDriveContent] = useState(data.driveContent || '');
  const [activeTab, setActiveTab] = useState('output');

  const getOutputTypeInfo = (type) => {
    const types = {
      'display': { icon: <FileTextOutlined />, label: 'Display', tag: 'Preview' },
      'save': { icon: <DatabaseOutlined />, label: 'Database', tag: 'DB' },
      'html': { icon: <CodeOutlined />, label: 'Shortcode', tag: 'HTML' },
      'webhook': { icon: <LinkOutlined />, label: 'Webhook', tag: 'HTTP' },
      'googleSheets': { icon: <Icon icon="mdi:google-spreadsheet" />, label: 'Google Sheets', tag: 'Sheets' },
      'googleDrive': { icon: <Icon icon="mdi:google-drive" />, label: 'Google Drive', tag: 'Drive' }
    };
    return types[type] || types['display'];
  };

  const nodes = useNodes();
  const edges = useEdges();

  useEffect(() => {
    if (outputType === 'googleSheets') {
      fetchGoogleSheets();
    } else if (outputType === 'googleDrive') {
      fetchGoogleDriveFolders();
    }
  }, [outputType]);

  useEffect(() => {
    if (workflowId) {
      setWorkflowIdState(workflowId);
    }
  }, [workflowId]);

  useEffect(() => {
    if (data.workflowId) {
      setWorkflowIdState(data.workflowId);
    }
  }, [data.workflowId]);

  useEffect(() => {
    if (selectedSpreadsheet) {
      fetchSheetTabs(selectedSpreadsheet);
    }
  }, [selectedSpreadsheet]);

  useEffect(() => {
    if (selectedSpreadsheet && (selectedSheetTab !== undefined && selectedSheetTab !== null)) {
      fetchSheetColumns();
    }
  }, [selectedSpreadsheet, selectedSheetTab]);

  useEffect(() => {
    fetchExistingTables();
  }, []);

  useEffect(() => {
    if (selectedTable && !isNewTable) {
      fetchTableColumns(selectedTable);
    }
  }, [selectedTable, isNewTable]);

  useEffect(() => {
    setDisplayOutput(data.displayOutput || "Output will be displayed here after execution");
  }, [data.displayOutput]);

  useEffect(() => {
    data.updateNodeData(id, {
      nodeName,
      outputType,
      selectedTable,
      webhookUrl,
      displayOutput,
      delayEnabled,
      delayValue,
      delayUnit,
      columns,
      webhookKeys,
      workflowId: workflowIdState,
      selectedSpreadsheet,
      selectedSheetTab,
      columnMappings, 
      driveItemType,
    });
  }, [nodeName, outputType, selectedTable, webhookUrl, displayOutput, delayEnabled, delayValue, delayUnit, columns, workflowId, webhookKeys, selectedSpreadsheet, selectedSheetTab, columnMappings, driveItemType]);

  
  // Create a new component for unified mapping
  const UnifiedMappingField = ({ 
    fieldName, 
    fieldValue, 
    onMappingChange,
    availableInputs,
    onDelete,
    showDelete = false
  }) => {
    return (
      <div style={{ 
        display: 'flex', 
        gap: '8px',
        marginBottom: 8,
        padding: 8,
        background: '#f5f5f5',
        borderRadius: 6
      }}>
        <div style={{ flex: 1 }}>
          <Text strong>{fieldName}</Text>
          <Input
            value={fieldValue}
            onChange={(e) => onMappingChange(fieldName, e.target.value)}
            placeholder="Select input or enter value"
            style={{ marginTop: 4 }}
            className="nodrag"
            addonAfter={
              <NodeInputHandler
                nodeId={id}
                onInsert={(tag) => onMappingChange(fieldName, tag)}
                customInputs={availableInputs}
                showInputTag={false}
              />
            }
          />
        </div>
        {showDelete && (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => onDelete(fieldName)}
          />
        )}
      </div>
    );
  };

  // Unified mapping handler
  const handleUnifiedMapping = (fieldName, value, type) => {
    switch (type) {
      case 'save':
        const newColumns = columns.map(col => 
          col.name === fieldName ? { ...col, mapping: value } : col
        );
        setColumns(newColumns);
        break;
        
      case 'googleSheets':
        setColumnMappings(prev => ({
          ...prev,
          [fieldName]: value
        }));
        break;
        
      case 'webhook':
        setWebhookKeys(prev => 
          prev.map(k => k.key === fieldName ? { ...k, mapping: value } : k)
        );
        break;
    }
  };
  
  const fetchExistingTables = async () => {

    if (data.isViewer) {
      return;
    }

    try {
      const response = await adminInstance.get('/tables');
      setExistingTables(['Create New Table', ...response.data]);
    } catch (error) {
      console.error('Error fetching existing tables:', error);
      message.error('Failed to fetch existing tables');
    }
  };

  const fetchTableColumns = async (table) => {

    if (data.isViewer) {
      return;
    }

    try {
      const response = await adminInstance.get(`/table-structure?table=${table}`);
      setColumns(response.data.map(col => ({
        name: col.name,
        type: col.type,
        mapping: data.columns?.find(c => c.name === col.name)?.mapping || ''
      })).filter(col => !['id', 'created_at'].includes(col.name)));
    } catch (error) {
      console.error('Error fetching table structure:', error);
      message.error('Failed to fetch table structure');
    }
  };

  const handleTableSelection = (value) => {
    if (value === 'Create New Table') {
      setIsNewTable(true);
      setSelectedTable('');
      setColumns([]);
    } else {
      setIsNewTable(false);
      setSelectedTable(value);
    }
  };

  const handleCreateNewTable = async () => {
    try {
      await form.validateFields();
      const response = await adminInstance.post('/tables', { 
        tableName: newTableName,
        columns: columns
      });
      if (response.data.success) {
        setExistingTables([...existingTables, response.data.tableName]);
        setSelectedTable(response.data.tableName);
        setIsNewTable(false);
        form.resetFields();
        setIsModalVisible(false);
        message.success('New table created successfully');
      }
    } catch (error) {
      console.error('Error creating new table:', error);
      message.error('Failed to create new table');
    }
  };

  const handleColumnAdd = (values) => {
    const newColumn = {
      name: values.name,
      type: values.type,
      mapping: ''
    };
    setColumns([...columns, newColumn]);
    setIsColumnModalVisible(false);
  };

  const handleColumnDelete = (index) => {
    const newColumns = columns.filter((_, i) => i !== index);
    setColumns(newColumns);
  };


  const handleWebhookUrlChange = (e) => {
    setWebhookUrl(e.target.value);
  };

  const handleTestWebhook = async () => {
    setIsTestingWebhook(true);
    try {
      const response = await adminInstance.post(`${config.apiSettings.root}wp-ai-workflows/v1/webhook-config/test`, {
        url: webhookUrl,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.data.status >= 200 && response.data.status < 300) {
        message.success('Webhook test successful!');
      } else {
        message.warning(`Webhook test received a non-200 response: ${response.data.status}`);
      }
    } catch (error) {
      console.error('Error testing webhook:', error);
      message.error('Failed to test webhook');
    } finally {
      setIsTestingWebhook(false);
    }
  };

  const handleWebhookKeyAdd = () => {
    if (newWebhookKey) {
      setWebhookKeys([...webhookKeys, { key: newWebhookKey, mapping: '' }]);
      setNewWebhookKey('');
      setIsWebhookKeyModalVisible(false);
    }
  };

  const handleWebhookKeyDelete = (keyToDelete) => {
    setWebhookKeys(webhookKeys.filter(k => k.key !== keyToDelete));
  };


  const fetchGoogleSheets = async () => {

    if (data.isViewer) {
      return;
    }

    try {
      const response = await adminInstance.get('/google-sheets');
      setSpreadsheets(response.data);
    } catch (error) {
      if (!handleGoogleAPIError(error)) {
        message.error('Failed to fetch Google Sheets');
      }
      setSpreadsheets([]);
    }
  };

  const fetchSheetTabs = async (sheetId) => {

    if (data.isViewer) {
      return;
    }

    try {
      const response = await adminInstance.get(`/google-sheets/${sheetId}/tabs`);
      setSheetTabs(response.data);
    } catch (error) {
      if (!handleGoogleAPIError(error)) {
        message.error('Failed to fetch sheet tabs');
      }
      setSheetTabs([]);
    }
  };

  const fetchSheetColumns = async () => {

    if (data.isViewer) {
      return;
    }

    console.log('Fetching sheet columns:', { selectedSpreadsheet, selectedSheetTab });
    if (!selectedSpreadsheet || selectedSheetTab === undefined || selectedSheetTab === null) {
      message.warning('Please select both a spreadsheet and a sheet tab first.');
      return;
    }
  
    setIsLoadingColumns(true);
    try {
      const response = await adminInstance.get(`/google-sheets/${selectedSpreadsheet}/tabs/${selectedSheetTab}/columns`);
      console.log('Sheet columns response:', response.data);
      setSheetColumns(response.data);
      // Initialize column mappings for new columns
      const newMappings = {};
      response.data.forEach(column => {
        if (!columnMappings[column]) {
          newMappings[column] = '';
        }
      });
      setColumnMappings(prev => ({ ...prev, ...newMappings }));
      message.success('Column structure loaded successfully');
    } catch (error) {
      console.error('Error fetching sheet columns:', error);
      if (error.response && error.response.data && error.response.data.message) {
        message.error(`Failed to fetch sheet columns: ${error.response.data.message}`);
      } else {
        message.error('Failed to fetch sheet columns');
      }
    } finally {
      setIsLoadingColumns(false);
    }
  };

  const handleSpreadsheetChange = (value) => {
    setSelectedSpreadsheet(value);
    setSelectedSheetTab(''); // Reset sheet tab when spreadsheet changes
    setSheetColumns([]); // Clear columns when spreadsheet changes
    data.updateNodeData(id, { selectedSpreadsheet: value, selectedSheetTab: '', columnMappings: {} });
  };

  const handleSheetTabChange = (value) => {
    setSelectedSheetTab(value);
    setSheetColumns([]); // Clear columns when sheet tab changes
    data.updateNodeData(id, { selectedSheetTab: value, columnMappings: {} });
  };


  const fetchGoogleDriveFolders = async () => {

    if (data.isViewer) {
      return;
    }
    
    setIsFetchingFolders(true);
    try {
        const response = await adminInstance.get('/google-drive-folders');
        if (response.data && Array.isArray(response.data)) {
            setDriveItems({ folders: response.data });
        }
    } catch (error) {
        if (!handleGoogleAPIError(error)) {
            message.error('Failed to fetch Google Drive folders');
        }
        setDriveItems({ folders: [] });
    } finally {
        setIsFetchingFolders(false);
    }
  };

  const renderGoogleSheetsOutput = () => (
    <>
        {(!spreadsheets || spreadsheets.length === 0) && (
      <GoogleIntegrationStatus>
        Configure Google Sheets integration to use this output
      </GoogleIntegrationStatus>
    )}
      <Select
        style={{ width: '100%', marginBottom: 8 }}
        value={selectedSpreadsheet}
        onChange={handleSpreadsheetChange}
        placeholder="Select a spreadsheet"
        className="nodrag"
        showSearch
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {spreadsheets.map(sheet => (
          <Option key={sheet.id} value={sheet.id}>{sheet.name}</Option>
        ))}
      </Select>
      <Select
        style={{ width: '100%', marginBottom: 8 }}
        value={selectedSheetTab}
        onChange={handleSheetTabChange}
        placeholder="Select a sheet tab"
        disabled={!selectedSpreadsheet}
        className="nodrag"
        showSearch
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {sheetTabs.map(tab => (
          <Option key={tab.id} value={tab.id}>{tab.name}</Option>
        ))}
      </Select>
      <Button 
        onClick={fetchSheetColumns} 
        loading={isLoadingColumns}
        style={{ marginBottom: 8 }}
        icon={<ReloadOutlined />}
        disabled={!selectedSpreadsheet || !selectedSheetTab}
      >
        Load Column Structure
      </Button>
      <Table
      dataSource={sheetColumns.map(column => ({ column, mapping: columnMappings[column] || '' }))}
      columns={[
        {
          title: 'Column',
          dataIndex: 'column',
          key: 'column',
        },
        {
          title: 'Mapping',
          dataIndex: 'mapping',
          key: 'mapping',
          render: (text, record) => (
            <Input
              value={text}
              onChange={(e) => handleUnifiedMapping(record.column, e.target.value, 'googleSheets')}
              placeholder="Select input or enter value"
              className="nodrag"
              addonAfter={
                <NodeInputHandler
                  nodeId={id}
                  onInsert={(tag) => handleUnifiedMapping(record.column, tag, 'googleSheets')}
                  showInputTag={false}
                />
              }
            />
          )
        },
      ]}
      pagination={false}
      size="small"
    />
  </>
);

const renderGoogleDriveOutput = () => (
  <>
    {(!driveItems.folders || !driveItems.folders.length) && (
      <GoogleIntegrationStatus>
        Configure Google Drive integration to use this output
      </GoogleIntegrationStatus>
    )}
    <Form.Item 
      label={
        <Space>
          <span>Select Folder</span>
          <Tooltip title="Search and select a folder to save your file">
            <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
          </Tooltip>
        </Space>
      }
    >
      <Select
          style={{ width: '100%', marginBottom: 8 }}
          value={selectedDriveFolder}
          onChange={(value) => {
              setSelectedDriveFolder(value);
              data.updateNodeData(id, { selectedDriveFolder: value });
          }}
          placeholder={isFetchingFolders ? "Loading folders..." : "Search and select a folder"}
          className="nodrag"
          showSearch
          loading={isFetchingFolders}
          optionFilterProp="children"
          filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
      >
          {Array.isArray(driveItems.folders) && driveItems.folders.map(folder => (
              <Option key={folder.id} value={folder.id}>
                  {folder.name || folder.id}
              </Option>
          ))}
      </Select>
    </Form.Item>

    <Form.Item 
      label={
        <Space>
          <span>File Name</span>
          <Tooltip title="You can use input tags to create dynamic file names">
            <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
          </Tooltip>
        </Space>
      }
    >
      <GenericNodeContent
        id={id}
        data={{
          content: driveFileName,
          type: 'fileName'
        }}
        onChange={(nodeId, newData) => {
          setDriveFileName(newData.content);
          data.updateNodeData(id, { driveFileName: newData.content });
        }}
        placeholder="Enter file name (leave blank for auto-generated name)"
        nodes={nodes}
      />
    </Form.Item>

    <Form.Item label="File Format">
      <Select
        style={{ width: '100%', marginBottom: 8 }}
        value={driveFileFormat}
        onChange={(value) => {
          setDriveFileFormat(value);
          data.updateNodeData(id, { driveFileFormat: value });
        }}
        className="nodrag"
      >
        <Option value="txt">Text (.txt)</Option>
        <Option value="docx">Word Document (.docx)</Option>
        <Option value="csv">CSV (.csv)</Option>
        <Option value="pdf">PDF (.pdf)</Option>
      </Select>
    </Form.Item>

    <Form.Item 
      label={
        <Space>
          <span>Document Content</span>
          <Tooltip title="Format your document content and use input tags to include dynamic data">
            <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
          </Tooltip>
        </Space>
      }
    >
      <GenericNodeContent
        id={id}
        data={{
          content: driveContent,
          type: 'content'
        }}
        onChange={(nodeId, newData) => {
          setDriveContent(newData.content);
          data.updateNodeData(id, { driveContent: newData.content });
        }}
        placeholder="Enter document content or use input tags"
        nodes={nodes}
      />
    </Form.Item>

    <Form.Item label="Sharing Settings">
      <Select
        style={{ width: '100%', marginBottom: 8 }}
        value={sharingLevel}
        onChange={(value) => {
          setSharingLevel(value);
          data.updateNodeData(id, { sharingLevel: value });
        }}
        className="nodrag"
      >
        <Option value="private">Private (Only you)</Option>
        <Option value="anyone_read">Anyone with link can view</Option>
        <Option value="anyone_write">Anyone with link can edit</Option>
      </Select>
    </Form.Item>
  </>
);


  return (
    <>
      <Card 
        title={
          <Space>
            {getOutputTypeInfo(outputType).icon}
            <span>Output</span>
            <Tag color="blue">{getOutputTypeInfo(outputType).tag}</Tag>
          </Space>
        }
        style={{ width: 380 }}
        extra={
        <Space>
          <Tooltip title={delayEnabled ? "Output Delayed" : "Add Delay"}>
            <Button
              type={delayEnabled ? "primary" : "text"}
              icon={<FieldTimeOutlined />}
              onClick={() => {
                setDelayEnabled(!delayEnabled);  // Toggle delay
                if (!delayEnabled) {  // If enabling delay
                  setActiveTab('delay');  // Switch to delay tab
                }
              }}
            />
          </Tooltip>
          <Popconfirm
        title="Delete Node"
        description="Are you sure you want to delete this node?"
        onConfirm={() => data.onDelete(id)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }} > 
        <Button icon={<DeleteOutlined />} /> </Popconfirm>
        </Space>
        }
      >
        <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
        
        <Input
          placeholder="Node Name"
          value={nodeName}
          onChange={(e) => setNodeName(e.target.value)}
          style={{ marginBottom: 16 }}
          prefix={<InfoCircleOutlined style={{ color: '#bfbfbf' }} />}
          className="nodrag"
        />
  
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Output" key="output">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Space align="center" style={{ marginBottom: 4 }}>
                  <Text strong>Output Type</Text>
                  <Tooltip title="Select how to handle the output">
                    <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                  </Tooltip>
                </Space>
  
                <Select
                  style={{ width: '100%', marginBottom: 16 }}
                  value={outputType}
                  onChange={setOutputType}
                  className="nodrag"
                >
                  <Option value="display">
                    <Space>
                      <FileTextOutlined />
                      <span>Display Output</span>
                    </Space>
                  </Option>
                  <Option value="save">
                    <Space>
                      <DatabaseOutlined />
                      <span>Save to Database</span>
                    </Space>
                  </Option>
                  <Option value="html">
                    <Space>
                      <CodeOutlined />
                      <span>Generate Shortcode</span>
                    </Space>
                  </Option>
                  <Option value="webhook">
                    <Space>
                      <LinkOutlined />
                      <span>Send to Webhook</span>
                    </Space>
                  </Option>
                  <Option value="googleSheets">
                    <Space>
                      <Icon icon="mdi:google-spreadsheet" />
                      <span>Save to Google Sheets</span>
                    </Space>
                  </Option>
                  <Option value="googleDrive">
                    <Space>
                      <Icon icon="mdi:google-drive" />
                      <span>Save to Google Drive</span>
                    </Space>
                  </Option>
                </Select>
              </div>
  
              {/* Display Output Type Content */}
              {outputType === 'display' && (
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: 12, 
                  borderRadius: 6 
                }}>
                  <Input.TextArea
                    rows={4}
                    value={displayOutput}
                    readOnly
                    placeholder="Output will be displayed here after execution"
                    className="nodrag"
                  />
                </div>
              )}
  
              {/* Database Output Type Content */}
              {outputType === 'save' && (
                <div>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Select
                      style={{ width: '100%' }}
                      value={selectedTable || (isNewTable ? 'Create New Table' : undefined)}
                      onChange={handleTableSelection}
                      placeholder="Select a table or create new"
                      className="nodrag"
                    >
                      {existingTables.map(table => (
                        <Option key={table} value={table}>{table}</Option>
                      ))}
                    </Select>
  
                    {isNewTable && (
                      <Input
                        placeholder="Enter new table name"
                        value={newTableName}
                        onChange={(e) => setNewTableName(e.target.value)}
                        className="nodrag"
                      />
                    )}
  
                    <Table
                    dataSource={columns}
                    columns={[
                      { title: 'Name', dataIndex: 'name', key: 'name', width: '30%' },
                      { title: 'Type', dataIndex: 'type', key: 'type', width: '20%' },
                      {
                        title: 'Mapping', 
                        dataIndex: 'mapping', 
                        key: 'mapping',
                        width: '40%',
                        render: (text, record) => (
                          <Space direction="vertical" style={{ width: '100%', margin: 0 }}>
                            <Input
                              value={text}
                              onChange={(e) => handleUnifiedMapping(record.name, e.target.value, 'save')}
                              placeholder="Select input or enter value"
                              className="nodrag"
                            />
                            <NodeInputHandler
                              nodeId={id}
                              onInsert={(tag) => handleUnifiedMapping(record.name, tag, 'save')}
                              showInputTag={true}
                            />
                          </Space>
                        )
                      },
                        {
                          title: 'Action',
                          key: 'action',
                          width: '10%',
                          render: (_, record, index) => (
                            isNewTable && (
                              <Button 
                                onClick={() => handleColumnDelete(index)} 
                                icon={<DeleteOutlined />} 
                              />
                            )
                          ),
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
  
                    {isNewTable && (
                      <Space style={{ marginTop: 8 }}>
                        <Button 
                          icon={<PlusOutlined />}
                          onClick={() => setIsColumnModalVisible(true)}
                        >
                          Add Column
                        </Button>
                        {columns.length > 0 && (
                          <Button 
                            type="primary" 
                            onClick={handleCreateNewTable}
                          >
                            Create Table
                          </Button>
                        )}
                      </Space>
                    )}
                  </Space>
                </div>
              )}
  
              {/* Shortcode Output Type Content */}
              {outputType === 'html' && (
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: 12, 
                  borderRadius: 6 
                }}>
                  {workflowIdState ? (
                    <>
                      <Text>Use this shortcode to display the output:</Text>
                      <div style={{ marginTop: 8 }}>
                        <Input.TextArea
                          value={`[wp_ai_workflows_output id="${workflowIdState}"]`}
                          readOnly
                          style={{ marginBottom: 8 }}
                        />
                        <Button 
                          onClick={() => {
                            navigator.clipboard.writeText(`[wp_ai_workflows_output id="${workflowIdState}"]`);
                            message.success('Shortcode copied to clipboard');
                          }}
                          icon={<CopyOutlined />}
                        >
                          Copy Shortcode
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Alert
                      message="Save Workflow First"
                      description="The shortcode will be generated after you save the workflow."
                      type="info"
                      showIcon
                    />
                  )}
                </div>
              )}
  
              {/* Webhook Output Type Content */}
              {outputType === 'webhook' && (
                <div>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Input
                      prefix={<LinkOutlined />}
                      placeholder="Enter webhook URL"
                      value={webhookUrl}
                      onChange={handleWebhookUrlChange}
                      className="nodrag"
                    />
                    
                    <Space>
                      <Button 
                        icon={<SendOutlined />} 
                        onClick={handleTestWebhook} 
                        loading={isTestingWebhook}
                      >
                        Test Webhook
                      </Button>
                      <Button
                        icon={<InfoCircleOutlined />}
                        onClick={() => setIsExplanationModalVisible(true)}
                      >
                        How it works
                      </Button>
                    </Space>
  
                    <div style={{ 
                      background: '#f5f5f5', 
                      padding: 12, 
                      borderRadius: 6,
                      marginTop: 8
                    }}>
                      <Text strong>Data Mapping</Text>
                      <Table
                        dataSource={webhookKeys}
                        columns={[
                          { 
                            title: 'Key', 
                            dataIndex: 'key', 
                            key: 'key', 
                            width: '30%' 
                          },
                          {
                            title: 'Mapping', 
                            dataIndex: 'mapping', 
                            key: 'mapping',
                            width: '60%',
                            render: (text, record) => (
                              <Space direction="vertical" style={{ width: '100%', margin: 0 }}>
                                <Input
                                  value={text}
                                  onChange={(e) => handleUnifiedMapping(record.key, e.target.value, 'webhook')}
                                  placeholder="Select input or enter value"
                                  className="nodrag"
                                />
                                <NodeInputHandler
                                  nodeId={id}
                                  onInsert={(tag) => handleUnifiedMapping(record.key, tag, 'webhook')}
                                  showInputTag={true}
                                />
                              </Space>
                            )
                          },
                          {
                            title: 'Action',
                            key: 'action',
                            width: '10%',
                            render: (_, record) => (
                              <Button 
                                type="text"
                                danger
                                icon={<DeleteOutlined />} 
                                onClick={() => handleWebhookKeyDelete(record.key)} 
                              />
                            ),
                          },
                        ]}
                        pagination={false}
                        size="small"
                      />
                      <Button 
                        icon={<PlusOutlined />} 
                        onClick={() => setIsWebhookKeyModalVisible(true)}
                        style={{ marginTop: 8 }}
                      >
                        Add Key
                      </Button>
                    </div>
                  </Space>
                </div>
              )}
  
              {/* Google Sheets Output Type Content */}
              {outputType === 'googleSheets' && (
                <div>
                  {renderGoogleSheetsOutput()}
                </div>
              )}
  
              {/* Google Drive Output Type Content */}
              {outputType === 'googleDrive' && (
                <div>
                  {renderGoogleDriveOutput()}
                </div>
              )}
            </Space>
          </TabPane>
  
          {delayEnabled && (
            <TabPane tab="Delay" key="delay">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Alert
                  message="Output Delay"
                  description="The output will be delayed by the specified duration."
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  action={
                    <Button 
                      danger 
                      size="small" 
                      onClick={() => {
                        setDelayEnabled(false);
                        setActiveTab('output');
                      }}
                    >
                      Disable Delay
                    </Button>
                  }
                />

                <div style={{ 
                  background: '#f5f5f5', 
                  padding: 12, 
                  borderRadius: 6 
                }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text strong>Delay Duration</Text>
                      <Space style={{ width: '100%', marginTop: 8 }}>
                        <InputNumber
                          style={{ width: '60%' }}
                          value={delayValue}
                          onChange={setDelayValue}
                          min={1}
                          className="nodrag"
                        />
                        <Select
                          style={{ width: '40%', minWidth: '100px' }} // Added minWidth to ensure text fits
                          value={delayUnit}
                          onChange={setDelayUnit}
                          className="nodrag"
                          options={[
                            { value: 'minutes', label: 'Minutes' },
                            { value: 'hours', label: 'Hours' },
                            { value: 'days', label: 'Days' }
                          ]}
                        />
                      </Space>
                    </div>
                  </Space>
                </div>
              </Space>
            </TabPane>
          )}
        </Tabs>
  
        <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
      </Card>
  
      {/* Modals */}
      <Modal
        title="Add Column"
        visible={isColumnModalVisible}
        onCancel={() => setIsColumnModalVisible(false)}
        footer={null}
      >
        <Form onFinish={handleColumnAdd}>
          <Form.Item name="name" label="Column Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="type" label="Column Type" rules={[{ required: true }]}>
            <Select>
              <Option value="text">Text</Option>
              <Option value="number">Number</Option>
              <Option value="datetime">Date/Time</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Add Column
            </Button>
          </Form.Item>
        </Form>
      </Modal>
  
      <Modal
        title="Add Webhook Key"
        visible={isWebhookKeyModalVisible}
        onOk={handleWebhookKeyAdd}
        onCancel={() => setIsWebhookKeyModalVisible(false)}
      >
        <Input
          placeholder="Enter webhook key (e.g., Data/Name/First)"
          value={newWebhookKey}
          onChange={(e) => setNewWebhookKey(e.target.value)}
        />
      </Modal>
  
      <Modal
        title="How Outgoing Webhooks Work"
        visible={isExplanationModalVisible}
        onOk={() => setIsExplanationModalVisible(false)}
        onCancel={() => setIsExplanationModalVisible(false)}
        width={600}
      >
        <Typography>
          <Paragraph>
            Outgoing webhooks allow you to send data from your workflow to external services or APIs. Here's how it works:
          </Paragraph>
          <ol>
            <li>Define the webhook URL where you want to send the data.</li>
            <li>Create key/value pairs to structure your data.</li>
            <li>Map outputs from previous nodes to these keys.</li>
            <li>When the workflow reaches this node, it will send a POST request to the specified URL with the mapped data.</li>
          </ol>
          <Paragraph>
            <strong>Key Structure:</strong>
          </Paragraph>
          <ul>
            <li>Use forward slashes (/) to create nested objects. For example, "Data/Name/First" will create: {`{ "Data": { "Name": { "First": "value" } } }`}</li>
            <li>To create arrays, use numeric indices. For example, "Data/Items/0" and "Data/Items/1" will create: {`{ "Data": { "Items": ["value1", "value2"] } }`}</li>
          </ul>
        </Typography>
      </Modal>
    </>
  );
};

export default OutputNode;